import { AionCta } from "@/components/site/AionCta";
import { OG_IMAGES } from "@/lib/og-images";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Briefcase } from "lucide-react";
import { PageBreadcrumb } from "@/components/site/PageBreadcrumb";
import { RelatedKeywords } from "@/components/site/RelatedKeywords";
import { RelatedPages } from "@/components/site/RelatedPages";

const TITLE = "Raj Shamani Business — Companies, Ventures & Investments";
const DESC =
  "Raj Shamani business empire explained: Shamani Industries, Figuring Out Media, House of X, brand partnerships and 20+ startup investments — what each company does and how they connect.";
const URL = "https://rajshamani.life/raj-shamani-business";
const OG = OG_IMAGES.ventures;

const faq = [
  {
    q: "What is Raj Shamani's business?",
    a: "Raj Shamani runs and is involved in several businesses: Shamani Industries, the family polymer additives manufacturing company where he is a director; Figuring Out Media, the media company behind the Figuring Out podcast; and House of X, a D2C venture studio he co-founded. He also invests in early-stage Indian startups.",
  },
  {
    q: "Which company does Raj Shamani own?",
    a: "His core operating business is Shamani Industries, a manufacturing company in polymer additives and speciality chemicals. Alongside it he built Figuring Out Media and co-founded House of X.",
  },
  {
    q: "What does Shamani Industries do?",
    a: "Shamani Industries manufactures and distributes polymer additives and speciality chemicals for Indian and export markets. It is the family business Raj joined at 16 and remains the foundation of his commercial experience.",
  },
  {
    q: "What is House of X?",
    a: "House of X is a D2C venture studio Raj Shamani co-founded. Rather than only investing, it helps build consumer brands from the ground up — product, distribution, content and community.",
  },
  {
    q: "Does Raj Shamani invest in startups?",
    a: "Yes. He has invested in 20+ early-stage Indian startups across D2C, fintech, consumer and creator-economy categories, usually at seed or pre-seed stage.",
  },
  {
    q: "How many businesses does Raj Shamani have?",
    a: "Three that he actively operates or co-owns — Shamani Industries, Figuring Out Media and House of X — plus an angel portfolio of over twenty companies where he is an investor rather than an operator.",
  },
];

const companies = [
  {
    name: "Shamani Industries",
    role: "Director",
    to: "/shamani-industries",
    desc: "Family-owned polymer additives and speciality chemicals manufacturer serving Indian and export markets. The oldest business in the group and the one Raj joined at 16.",
  },
  {
    name: "Figuring Out Media",
    role: "Founder",
    to: "/raj-shamani-podcast",
    desc: "The media company behind Figuring Out, one of India's most-watched long-form business interview shows, plus the surrounding content and distribution operation.",
  },
  {
    name: "House of X",
    role: "Co-founder",
    to: "/house-of-x",
    desc: "A D2C venture studio that builds and backs consumer brands, applying the distribution and audience advantages the media business created.",
  },
  {
    name: "Angel portfolio",
    role: "Investor",
    to: "/ventures",
    desc: "20+ early-stage Indian startups across D2C, fintech, consumer and creator economy, backed at seed and pre-seed stage.",
  },
];

export const Route = createFileRoute("/raj-shamani-business")({
  head: () => ({
    meta: [
      { title: TITLE, key: "title" },
      { name: "description", content: DESC, key: "description" },
      { property: "og:title", content: TITLE, key: "og:title" },
      { property: "og:description", content: DESC, key: "og:description" },
      { property: "og:url", content: URL, key: "og:url" },
      { property: "og:type", content: "article", key: "og:type" },
      { property: "og:image", content: OG, key: "og:image" },
      { property: "og:image:alt", content: "Raj Shamani businesses and companies", key: "og:image:alt" },
      { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
      { name: "twitter:title", content: TITLE, key: "twitter:title" },
      { name: "twitter:description", content: DESC, key: "twitter:description" },
      { name: "twitter:image", content: OG, key: "twitter:image" },
      { property: "og:locale", content: "en_IN", key: "og:locale" },
      { name: "twitter:site", content: "@rajshamani", key: "twitter:site" },
      { name: "twitter:creator", content: "@rajshamani", key: "twitter:creator" },
      { name: "geo.region", content: "IN", key: "geo.region" },
    ],
    links: [{ rel: "canonical", href: URL }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebPage",
          "@id": `${URL}#webpage`,
          name: TITLE,
          headline: TITLE,
          description: DESC,
          url: URL,
          inLanguage: "en",
          isPartOf: { "@type": "WebSite", "@id": "https://rajshamani.life/#website" },
          breadcrumb: { "@id": `${URL}#breadcrumb` },
          publisher: { "@type": "Organization", "@id": "https://rajshamani.life/#organization" },
          primaryImageOfPage: { "@type": "ImageObject", url: OG },
          image: OG,
          about: {
            "@type": "Person",
            "@id": "https://rajshamani.life/about#person",
            name: "Raj Shamani",
            url: "https://rajshamani.life/about",
          },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          "@id": `${URL}#faq`,
          mainEntity: faq.map((f) => ({
            "@type": "Question",
            name: f.q,
            acceptedAnswer: { "@type": "Answer", text: f.a },
          })),
        }),
      },
    ],
  }),
  component: BusinessPage,
});

function BusinessPage() {
  return (
    <div>
      <PageBreadcrumb current="Raj Shamani Business" />

      <section className="bg-noir border-b border-border/50">
        <div className="mx-auto max-w-5xl px-6 py-24">
          <div className="text-xs tracking-widest uppercase text-gold mb-4 inline-flex items-center gap-2">
            <Briefcase size={14} /> Companies & ventures
          </div>
          <h1 className="text-4xl md:text-6xl font-bold leading-tight">
            Raj Shamani <span className="text-gradient-gold">business</span>: every company he runs
          </h1>
          <p className="mt-8 text-lg text-muted-foreground max-w-3xl leading-relaxed">
            Raj Shamani is usually described as a podcaster, but the podcast is the newest part of
            the picture. The businesses behind his name are a manufacturing company, a media
            company, a D2C venture studio and an angel portfolio — four very different engines
            that feed each other.
          </p>
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 py-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">The companies, one by one</h2>
        <div className="grid gap-6 md:grid-cols-2">
          {companies.map((c) => (
            <Link
              key={c.name}
              to={c.to}
              className="rounded-xl border border-border bg-card p-6 hover:border-gold/60 transition-colors"
            >
              <div className="text-xs uppercase tracking-wide text-gold mb-2">{c.role}</div>
              <h3 className="text-xl font-bold mb-2">{c.name}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{c.desc}</p>
            </Link>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-6">How the businesses connect</h2>
        <p className="text-muted-foreground max-w-3xl mb-6 leading-relaxed">
          The manufacturing business came first and taught the fundamentals — margins, working
          capital, distribution and the unglamorous reality of selling to other businesses. The
          media business came second and produced something manufacturing never could: direct
          attention from founders, investors and consumers across India.
        </p>
        <p className="text-muted-foreground max-w-3xl mb-6 leading-relaxed">
          House of X is the bridge. A venture studio only works if it has an unfair advantage in
          one of the expensive parts of brand building, and in this case that advantage is
          distribution plus a live feedback channel to an audience. The angel portfolio sits on
          top, giving exposure to categories the studio does not build in directly.
        </p>
        <p className="text-muted-foreground max-w-3xl leading-relaxed">
          Read the detail on{" "}
          <Link to="/shamani-industries" className="text-gold underline underline-offset-4">
            Shamani Industries
          </Link>
          ,{" "}
          <Link to="/house-of-x" className="text-gold underline underline-offset-4">
            House of X
          </Link>{" "}
          and how the earnings break down on the{" "}
          <Link to="/raj-shamani-net-worth" className="text-gold underline underline-offset-4">
            net worth page
          </Link>
          .
        </p>
      </section>

      <section className="mx-auto max-w-5xl px-6 pb-20">
        <h2 className="text-3xl md:text-4xl font-bold mb-8">Frequently asked questions</h2>
        <div className="space-y-6">
          {faq.map((f) => (
            <div key={f.q} className="rounded-xl border border-border bg-card p-6">
              <h3 className="font-bold mb-2">{f.q}</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <AionCta />
      <RelatedKeywords currentPath="/raj-shamani-business" />

      <RelatedPages pages={["aion", "about", "ventures", "podcast", "faq", "contact"]} />
    </div>
  );
}
